import { useGetAllAssets } from "@features/ax/assetRegistry/systems/hooks";
import { Asset } from "@features/ax/assetRegistry/systems/types";
import { getConditionLabel } from "@features/ax/utils";
import { GenericTable } from "@shared/components/GenericTable";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { PAGE_SIZE } from "@shared/utils/constants";
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const getRiskColor = (score: number): string => {
  if (score <= 30) return "bg-green-100 text-green-800";
  if (score <= 60) return "bg-yellow-100 text-yellow-800";
  return "bg-red-100 text-red-800";
};

const getConditionColor = (score: number | null | undefined): string => {
  if (score === null || score === undefined) return "bg-gray-100 text-gray-800"; // NA
  if (score > 80) return "bg-green-100 text-green-800"; // Good
  if (score >= 40) return "bg-yellow-100 text-yellow-800"; // Fair
  return "bg-red-100 text-red-800"; // Poor
};

const getConditionText = (score: number | null | undefined): string => {
  if (score === null || score === undefined) return "NA";
  if (score > 80) return "Good";
  if (score >= 40) return "Fair";
  return "Poor";
};

export const AssetsTable: React.FC = () => {
  const { systemId } = useParams();
  const [filters, setFilters] = useState({
    search: "",
    systemNetwork: "all",
    location: "all",
    conditionRange: "all",
    riskRange: "all",
    uptimeRange: "all",
    page: 1,
  });
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const { data: assetsData } = useGetAllAssets(
    filters.page,
    PAGE_SIZE,
    Number(systemId),
    filters.search
  );

  const columns: TableColumn<Asset>[] = useMemo(
    () => [
      {
        key: "assetNo",
        header: "Asset ID",
        headerTooltip: "The unique identifier for this asset.",
        type: "custom",
        render: (value: string) => <span className="ml-3">{value}</span>,
      },
      {
        key: "assetName",
        header: "Asset Name",
        headerTooltip: "The common name used to identify this asset.",
        type: "text",
      },
      {
        key: "systemName",
        header: "System/Network",
        headerTooltip:
          "The parent system or network this asset is a component of.",
        type: "text",
      },
      {
        key: "location",
        header: "Location",
        headerTooltip: "The specific physical location of the asset.",
        type: "custom",
        render: (value) => <HoverRevealText text={value} />,
      },
      {
        key: "conditionScores",
        header: "Condition Score",
        type: "custom",
        render: (value: number) => (
          <div className="flex items-center gap-2">
            <span className="font-medium">{value}</span>
            <Badge
              variant="secondary"
              className={getConditionLabel(value).color}
            >
              {getConditionLabel(value).label}
            </Badge>
          </div>
        ),
        headerTooltip:
          "The physical health of the asset, based on the most recent inspection. Higher is better.",
      },
      {
        key: "riskScore",
        header: "Risk Score",
        type: "custom",
        render: (value: number, row: any) => (
          <div className="flex items-center gap-2">
            <span className="font-medium">{value}</span>
            <Badge variant="secondary" className={getRiskColor(value)}>
              {row.riskStatus}
            </Badge>
          </div>
        ),
        headerTooltip:
          "The overall risk this asset poses, combining its likelihood of failure with the consequences of that failure.",
      },
    ],
    []
  );

  const actions: TableAction<Asset>[] = useMemo(
    () => [
      {
        label: "View Details",
        onClick: (item) => {
          navigate(`/ax/asset-registry/assets/view/${item.id}`);
        },
      },
    ],
    []
  );

  const handleSearch = (searchTerm: string) => {
    setFilters((prev) => ({ ...prev, search: searchTerm, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  // Pagination logic
  const totalItems = assetsData?.results?.length;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startIndex = (filters.page - 1) * PAGE_SIZE;

  const pagination = useMemo(
    () => ({
      currentPage: filters.page,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: startIndex + 1,
      endItem: Math.min(filters.page * PAGE_SIZE, totalItems),
    }),
    [filters.page, totalPages, totalItems, startIndex]
  );

  return (
    <div className="space-y-6">
      <GenericTable
        data={assetsData?.results}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search by asset name, ID, system, or location..."
        searchParamName="search"
        searchDebounceMs={300}
        onSearch={handleSearch}
        pagination={pagination}
        onPageChange={handlePageChange}
        actions={actions}
        actionsType="dropdown"
      />
    </div>
  );
};
